/**
 * Pure-logic primitives for the slice-to-slice contour deformation
 * suggestion feature. These functions operate on raw SDF pixel arrays
 * and texture geometry only; they have no engine, view, or React
 * dependencies and are unit-testable in isolation.
 *
 * Conventions:
 *  - SDF pixels are stored as one Uint8Array per Z slice of the
 *    texture grid, row-major (sizeX * sizeY bytes per slice).
 *  - The iso-threshold separating inside (>=) from outside (<) the
 *    structure is {@link ContourTexture.SDF_THRESHOLD}.
 *  - "Empty" means the slice has no pixel at or above that threshold
 *    (see {@link SignedDistanceField.isSliceEmpty}).
 *  - Polygon coordinates returned by `extractSlicePolygons` (in
 *    `marchingSquares.ts`) are in the texture grid's voxel-centered
 *    continuous coordinate system, ready to feed into
 *    `Grid.toWorldSpace`.
 */

/** Edit-history label of an accepted suggestion derived between contoured slices. */
export const CONTOUR_AUTO_SUGGESTION_OPERATION = 'slice-to-slice-deformation';

/**
 * Edit-history label of an accepted suggestion beyond the first or last
 * contoured slice: a verbatim copy of the nearest contoured slice.
 */
export const NEAREST_CONTOUR_COPY_OPERATION = 'copy-of-nearest-contour';

/**
 * Delay before a suggestion is computed and rendered after the user
 * lands on a new slice. This gives the user time to scroll past
 * intermediate slices without seeing the ghost flicker.
 */
export const CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS = 500;

// ─── Multi-slice B-spline interpolation ───────────────────────────────────────

/**
 * Cubic uniform B-spline basis functions evaluated at parameter t ∈ [0, 1].
 * These four weights are the standard cubic B-spline kernel over the
 * segment between knots k+1 and k+2, evaluated at position (k+1 + t).
 *
 * The weights are an *approximating* (not interpolating) kernel: they
 * are non-negative and form a partition of unity (sum to exactly 1.0
 * for any t in [0, 1]). Any caller's weighted combination of inputs is
 * therefore a convex combination, which keeps the result within the
 * input range and yields a smooth in-between — see
 * {@link blendSlicesBSpline} for how this is exploited in SDF space.
 */
export function cubicBSplineWeights(
    t: number
): [number, number, number, number] {
    if (!Number.isFinite(t)) {
        throw new Error(
            `cubicBSplineWeights: t must be a finite number, got ${t}`
        );
    }
    if (t < 0 || t > 1) {
        throw new Error(`cubicBSplineWeights: t must be in [0, 1], got ${t}`);
    }

    const t2 = t * t;
    const t3 = t2 * t;
    const oneMinusT = 1 - t;
    const oneMinusT3 = oneMinusT * oneMinusT * oneMinusT;

    const w0 = oneMinusT3 / 6;
    const w1 = (3 * t3 - 6 * t2 + 4) / 6;
    const w2 = (-3 * t3 + 3 * t2 + 3 * t + 1) / 6;
    const w3 = t3 / 6;

    return [w0, w1, w2, w3];
}

/**
 * Input descriptor for a single non-empty slice participating in
 * the B-spline interpolation.
 */
export type SliceWithPosition = {
    data: Uint8Array;
    z: number;
};

/**
 * Multi-slice B-spline interpolation in SDF space.
 *
 * Given up to 4 non-empty slices surrounding the target Z,
 * computes a per-pixel weighted sum using cubic B-spline basis
 * functions ({@link cubicBSplineWeights}).
 *
 * Why this is valid in SDF space: SDFs are smooth scalar fields, and
 * because the B-spline weights form a convex partition of unity (see
 * {@link cubicBSplineWeights}) the per-pixel blend is a convex
 * combination of valid SDFs — itself a valid SDF, whose iso-surface is
 * a smooth morph between the source iso-surfaces. The cubic kernel
 * gives C² continuity along Z, eliminating the slope discontinuities
 * inherent in linear (midpoint) blending and incorporating shape
 * context from further neighbours, for visually smoother
 * slice-to-slice transitions.
 *
 * Graceful degradation:
 *  - 4 slices: full cubic B-spline
 *  - 3 slices: cubic B-spline with renormalized weights
 *  - 2 slices: linear interpolation (distance-weighted)
 *  - 1 slice: direct copy
 *
 * Performance: O(pixels) with at most 4 blend ops per pixel.
 * Single allocation for the output buffer; allocation-free inside
 * the inner loop.
 *
 * @param slices Non-empty slices (any order; sorted internally by Z).
 * @param targetZ The Z index to interpolate at.
 * @returns Interpolated SDF slice as Uint8Array.
 */
export function blendSlicesBSpline(
    slices: SliceWithPosition[],
    targetZ: number
): Uint8Array {
    if (slices.length === 0) {
        throw new Error('blendSlicesBSpline: no input slices');
    }

    const sliceLength = slices[0].data.length;

    // Validate all slices have same length (before sorting so index is meaningful)
    for (let i = 1; i < slices.length; i++) {
        if (slices[i].data.length !== sliceLength) {
            throw new Error(
                `blendSlicesBSpline: slice byte length mismatch at index ${i} ` +
                    `(expected ${sliceLength}, got ${slices[i].data.length})`
            );
        }
    }

    // Sort by ascending Z so all downstream logic (window
    // selection, B-spline parameter computation, weight assignment)
    // sees slices in the expected order.
    const sorted = slices.slice().sort((a, b) => a.z - b.z);

    // Fallback: single slice → direct copy
    if (sorted.length === 1) {
        return new Uint8Array(sorted[0].data);
    }

    // Validate targetZ is within the range of provided slices
    const minZ = sorted[0].z;
    const maxZ = sorted[sorted.length - 1].z;
    if (targetZ < minZ || targetZ > maxZ) {
        throw new Error(
            `blendSlicesBSpline: targetZ (${targetZ}) is outside the slice range [${minZ}, ${maxZ}]`
        );
    }

    // Fallback: two slices → distance-weighted linear interpolation
    if (sorted.length === 2) {
        return blendTwoSlicesLinear(
            sorted[0].data,
            sorted[1].data,
            sorted[0].z,
            sorted[1].z,
            targetZ
        );
    }

    // 3 or 4 slices: cubic B-spline interpolation
    // Find the parameter t within the local support window.
    // For 4 slices at positions [z0, z1, z2, z3], the segment of
    // interest is [z1, z2] with t mapping targetZ linearly into [0, 1].
    // For 3 slices, we select the best triplet arrangement.

    // Select at most 4 slices for the cubic B-spline. When more
    // than 4 are provided, pick the window of 4 consecutive slices
    // whose inner pair's midpoint is closest to targetZ
    let selected = sorted;
    if (sorted.length > 4) {
        selected = selectBracket(sorted, targetZ);
    }

    let weights: number[];

    if (selected.length >= 4) {
        // Full 4-point B-spline: interpolation segment is between
        // the 2nd and 3rd control points (selected[1] and selected[2]).
        const z1 = selected[1].z;
        const z2 = selected[2].z;

        if (targetZ < z1 || targetZ > z2) {
            throw new Error(
                `blendSlicesBSpline: targetZ (${targetZ}) is not within the interpolation segment [${z1}, ${z2}]`
            );
        }

        const span = z2 - z1;
        const t = span > 0 ? (targetZ - z1) / span : 0.5;
        const [w0, w1, w2, w3] = cubicBSplineWeights(t);
        weights = [w0, w1, w2, w3];
    } else {
        // 3 slices: determine position of targetZ relative to the slices
        // and assign B-spline weights with one endpoint missing.
        weights = computeThreeSliceWeights(selected, targetZ);
    }

    // Normalize weights (handles cases where fewer than 4 are used)
    const wSum = weights.reduce((a, b) => a + b, 0);
    if (wSum <= 0) {
        // Degenerate case — fall back to equal blend
        const eq = 1 / selected.length;
        weights = selected.map(() => eq);
    } else if (Math.abs(wSum - 1.0) > 1e-10) {
        for (let i = 0; i < weights.length; i++) {
            weights[i] /= wSum;
        }
    }

    // Per-pixel weighted sum
    const out = new Uint8Array(sliceLength);
    for (let px = 0; px < sliceLength; px++) {
        let val = 0;
        for (let s = 0; s < weights.length; s++) {
            val += selected[s].data[px] * weights[s];
        }
        // Clamp to [0, 255] with rounding
        out[px] = val <= 0 ? 0 : val >= 255 ? 255 : Math.round(val);
    }
    return out;
}

/**
 * Select the best window of 4 consecutive slices whose inner pair
 * best brackets targetZ. Slices must be sorted by ascending Z.
 */
export function selectBracket(
    sorted: SliceWithPosition[],
    targetZ: number
): SliceWithPosition[] {
    let bestStart = 0;
    let bestDist = Infinity;
    for (let i = 0; i <= sorted.length - 4; i++) {
        const mid = (sorted[i + 1].z + sorted[i + 2].z) / 2;
        const dist = Math.abs(targetZ - mid);
        if (dist < bestDist) {
            bestDist = dist;
            bestStart = i;
        }
    }
    return sorted.slice(bestStart, bestStart + 4);
}

/**
 * Distance-weighted linear interpolation between two slices.
 * When targetZ is equidistant this reduces to midpoint blending.
 */
export function blendTwoSlicesLinear(
    a: Uint8Array,
    b: Uint8Array,
    zA: number,
    zB: number,
    targetZ: number
): Uint8Array {
    if (a.length !== b.length) {
        throw new Error(
            `blendTwoSlicesLinear: slice length mismatch (${a.length} vs ${b.length})`
        );
    }
    if (a.length === 0) {
        throw new Error('blendTwoSlicesLinear: slices must not be empty');
    }
    if (zB < zA) {
        throw new Error(
            `blendTwoSlicesLinear: zA (${zA}) must be <= zB (${zB})`
        );
    }

    const span = zB - zA;
    // t = 0 → fully slice A, t = 1 → fully slice B
    const t = span > 0 ? Math.max(0, Math.min(1, (targetZ - zA) / span)) : 0.5;
    const wA = 1 - t;
    const wB = t;

    const out = new Uint8Array(a.length);
    for (let i = 0; i < a.length; i++) {
        const val = a[i] * wA + b[i] * wB;
        out[i] = val <= 0 ? 0 : val >= 255 ? 255 : Math.round(val);
    }
    return out;
}

/**
 * Compute B-spline weights for exactly 3 slices. We determine
 * which "slot" the target occupies relative to the 3 available
 * slices and map to the appropriate B-spline segment.
 */
export function computeThreeSliceWeights(
    slices: SliceWithPosition[],
    targetZ: number
): number[] {
    if (slices.length !== 3) {
        throw new Error(
            `computeThreeSliceWeights: expected exactly 3 slices, got ${slices.length}`
        );
    }

    const z0 = slices[0].z;
    const z1 = slices[1].z;
    const z2 = slices[2].z;

    if (z0 >= z1 || z1 >= z2) {
        throw new Error(
            `computeThreeSliceWeights: slices must be strictly sorted by ascending Z (got [${z0}, ${z1}, ${z2}])`
        );
    }

    if (targetZ < z0 || targetZ > z2) {
        throw new Error(
            `computeThreeSliceWeights: targetZ (${targetZ}) is outside the slice range [${z0}, ${z2}]`
        );
    }

    // Determine which segment targetZ falls in: [z0, z1] or [z1, z2]
    if (targetZ <= z1) {
        const span = z1 - z0;
        const t = (targetZ - z0) / span;
        const [, w1, w2, w3] = cubicBSplineWeights(t);
        return [w1, w2, w3];
    } else {
        const span = z2 - z1;
        const t = (targetZ - z1) / span;
        const [w0, w1, w2] = cubicBSplineWeights(t);
        return [w0, w1, w2];
    }
}
